import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ArrowsClockwise } from "@phosphor-icons/react";
import Breadcrumbs from "@dashboard/components/breadcrumbs/breadcrumbs";
import { UiButton } from "@domun/component-library";
import ConfirmModal from "@dashboard/components/confirm-modal";
import { DeliveryCheckoutSummary } from "../../components/delivery-checkout-summary/delivery-checkout-summary";
import { DeliveryPageWrapper } from "../../components/delivery-page-layout/delivery-page-layout.styled";
import { useDeliveryDetail } from "../../hooks/use-delivery-detail.hook";
import { resolveLogisticStateLabel } from "../../constants/logistic-states.constants";
import { getDeliveryDisplayId } from "../../utils/delivery-detail.utils";

const DeliveryDetail: FC = () => {
  const { t } = useTranslation();
  const {
    detail,
    calculation,
    selectedProvider,
    sizeLabel,
    typeLabel,
    linkedOrderId,
    partner,
    loading,
    cancelLoading,
    showCancelModal,
    setShowCancelModal,
    loadDetail,
    handlePay,
    handleCancel,
    openTracking,
    openGuide,
    isPendingPayment,
    breadcrumbs,
  } = useDeliveryDetail();

  if (loading && !detail) {
    return (
      <DeliveryPageWrapper>
        <p>...</p>
      </DeliveryPageWrapper>
    );
  }

  if (!detail || !calculation) {
    return (
      <DeliveryPageWrapper>
        <p>{t("delivery.home.loadError")}</p>
        <UiButton
          onClick={loadDetail}
          isLoading={loading}
          disabled={loading}
        >
          {t("delivery.detail.refresh")}
        </UiButton>
      </DeliveryPageWrapper>
    );
  }

  const displayId = getDeliveryDisplayId(detail);
  const statusLabel = resolveLogisticStateLabel(
    detail.state,
    detail.subState,
    t
  );

  return (
    <DeliveryPageWrapper>
      <div className="header">
        <Breadcrumbs items={breadcrumbs} backButton />
      </div>
      <h2>
        {t("delivery.detail.title")} #{displayId}
      </h2>

      <div className="detail-card">
        <div className="detail-card-header">
          <div className="detail-status">
            <span className="detail-status-label">
              {t("delivery.detail.status")}
            </span>
            <span className="detail-status-value">{statusLabel}</span>
            <span className="detail-status-id"># {displayId}</span>
          </div>
          <button
            type="button"
            className={`detail-refresh-btn${loading ? " spinning" : ""}`}
            title={t("delivery.detail.refresh")}
            aria-label={t("delivery.detail.refresh")}
            onClick={loadDetail}
            disabled={loading}
          >
            <ArrowsClockwise size={20} weight="bold" />
          </button>
        </div>

        <DeliveryCheckoutSummary
          calculation={calculation}
          selectedProvider={selectedProvider}
          sizeLabel={sizeLabel}
          typeLabel={typeLabel}
          linkedOrderId={linkedOrderId}
          userCountry={partner?.userCountry}
          variant="detail"
        />

        {detail.error && <p className="error-text">{detail.error}</p>}
      </div>

      <div className="actions">
        {isPendingPayment && (
          <UiButton onClick={handlePay} appereance="">
            {t("delivery.detail.pay")}
          </UiButton>
        )}
        {detail.raw?.trackingUrl && (
          <UiButton onClick={openTracking}>
            {t("delivery.detail.tracking")}
          </UiButton>
        )}
        {detail.guideUrl && (
          <UiButton onClick={openGuide}>{t("delivery.detail.guide")}</UiButton>
        )}
        <UiButton onClick={() => setShowCancelModal(true)}>
          {t("delivery.detail.cancel")}
        </UiButton>
      </div>

      {showCancelModal && (
        <ConfirmModal
          title={t("delivery.detail.cancel")}
          copy={t("delivery.detail.cancelConfirm")}
          continueLoading={cancelLoading}
          closeOnContinue={false}
          onContinue={handleCancel}
          onClose={() => !cancelLoading && setShowCancelModal(false)}
        />
      )}
    </DeliveryPageWrapper>
  );
};

export default DeliveryDetail;
